'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import Link from 'next/link';
import { ArrowRight, Check, ThumbsUp, ThumbsDown, Loader2, Calendar } from 'lucide-react';
import { useChat } from 'ai/react';
import CalendarLink from '@/components/ui/calendar-link';
import { Session } from 'next-auth';
import { useSession, SessionProvider } from 'next-auth/react';
import { trackPurchaseEvent } from '@/lib/purchase-tracking';

// Email validation function
const isValidEmail = (email: string): boolean => {
	if (!email || email.trim() !== email) return false;
	if (email.includes(' ')) return false;
	if (!email.includes('@')) return false;
	if (email.split('@').length !== 2) return false;
	const [localPart, domain] = email.split('@');
	if (!localPart || localPart.length === 0) return false;
	if (!domain || domain.length === 0) return false;
	if (!domain.includes('.')) return false;
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	return emailRegex.test(email);
};

// Static version that shows while session is loading
function StaticHeroSection() {
	const [email, setEmail] = useState('');

	const logos = [
		{ name: 'Instantly', src: '/instantly.webp' },
		{ name: 'Smartlead', src: '/smartlead.webp' },
		{ name: 'Clay', src: '/clay.webp' },
		{ name: 'Apollo', src: '/apollo.webp' },
		{ name: 'ReplyIO', src: '/replyio.webp' }
	];

	return (
		<section className="relative py-20 px-4 sm:px-6 lg:px-8">
			<div className="max-w-7xl mx-auto">
				<div className="text-center">
					<Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
						🚀 The New Way to Do Cold Email
					</Badge>
					<h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
						<span className="text-emerald-600 block">
							What Cold Email Infra is Best?
						</span>
						Stop Guessing. Try Everything.
					</h1>
					<p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
						While others juggle a few unreliable email providers or servers, we
						instantly route your emails across
						<strong className="text-emerald-600">
							{' '}
							100+ infrastructure partners
						</strong>{' '}
						adapting to spam filter changes hourly.
						<strong className="text-emerald-600">
							{' '}
							Improve deliverability by average of 15 to 30%
						</strong>{' '}
						with setup in just 5 minutes.
					</p>

					{/* Static Demo Section */}
					<div className="max-w-2xl mx-auto mb-8">
						<div className="space-y-4">
							<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
								<div className="relative max-w-md w-full">
									<Input
										type="email"
										placeholder="Enter your email - we'll prove it works"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										autoComplete="email"
										className="text-lg px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all duration-200 shadow-lg hover:shadow-xl"
									/>
								</div>
								<Button
									size="lg"
									disabled={true}
									className="text-lg px-8 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
								>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Initializing...
								</Button>
							</div>
							<p className="text-sm text-gray-500">
								☝️ <strong>DON'T BELIEVE US?</strong> Put your email above and see
								if we can land in your PRIMARY inbox (not spam!)
							</p>
						</div>
					</div>

					<div className="mt-8">
						<p className="text-sm text-gray-500 mb-4 font-medium">Works with:</p>
						<div className="flex flex-wrap justify-center items-center gap-6 max-w-4xl mx-auto">
							{logos.map((logo) => (
								<div
									key={logo.name}
									className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-sm border"
								>
									<Image
										src={logo.src}
										alt={`${logo.name} logo`}
										width={24}
										height={24}
										className="rounded-md"
									/>
									<span className="text-gray-700 font-medium">{logo.name}</span>
								</div>
							))}
							<div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
								<span className="text-gray-500 font-medium">and many more...</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

// Interactive version with full deliverability test functionality
function InteractiveHeroSection({
	sessionRef
}: {
	sessionRef: React.MutableRefObject<Session | null>;
}) {
	const [email, setEmail] = useState('');
	const [showDemo, setShowDemo] = useState(false);
	const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
	const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
	const [currentStepData, setCurrentStepData] = useState<Record<string, any>>({});
	const [isComplete, setIsComplete] = useState(false);
	const [testFailed, setTestFailed] = useState(false);

	const logos = [
		{ name: 'Instantly', src: '/instantly.webp' },
		{ name: 'Smartlead', src: '/smartlead.webp' },
		{ name: 'Clay', src: '/clay.webp' },
		{ name: 'Apollo', src: '/apollo.webp' },
		{ name: 'ReplyIO', src: '/replyio.webp' }
	];

	const {
		handleSubmit,
		isLoading,
		data: streamData
	} = useChat({
		api: `/api/sites/${sessionRef.current?.siteId || 'demo'}/email-deliverability-test`,
		onResponse: (res) => console.log('Deliverability test started:', res),
		onFinish: (msg) => {
			console.log('Deliverability test finished:', msg);
			setIsComplete(true);
		},
		onError: (error) => {
			console.error('Error during deliverability test:', error);
		}
	});

	// Process streaming data
	useEffect(() => {
		if (!streamData || streamData.length === 0) return;

		const latestData = streamData[streamData.length - 1];
		if (!latestData) return;

		try {
			const stepData = typeof latestData === 'string' ? JSON.parse(latestData) : latestData;
			console.log('Received step data:', stepData);

			if (stepData.step === 'test_complete' && stepData.final) {
				setIsComplete(true);
				if (stepData.status === 'failed') {
					setTestFailed(true);
				}
				return;
			}

			if (stepData.status === 'failed') {
				setTestFailed(true);
			}

			setCurrentStepData((prev) => ({
				...prev,
				[stepData.step]: stepData
			}));

			if (stepData.status === 'completed') {
				setCompletedSteps((prev) => {
					const newSet = new Set(Array.from(prev));
					newSet.add(stepData.step);
					return newSet;
				});
			}
		} catch (e) {
			console.warn('Failed to parse stream data:', e);
		}
	}, [streamData]);

	const handleDeliverabilityTest = () => {
		if (isValidEmail(email)) {
			trackPurchaseEvent({
				transaction_id: `leads_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
				value: 1,
				currency: 'USD',
				items: [
					{
						item_id: 'leads',
						item_name: email,
						category: 'Leads',
						quantity: 1,
						price: 1
					}
				],
				purchase_type: 'other',
				user_id: sessionRef.current?.user?.email || email,
				test_context: 'hero_section_challenge_button',
				landing_page: 'nosemail'
			});
		}

		// Original function logic continues...
		if (!isValidEmail(email) || !sessionRef.current?.siteId) return;

		setShowDemo(true);
		setCompletedSteps(new Set());
		setCurrentStepData({});
		setIsComplete(false);
		setFeedback(null);
		setTestFailed(false);

		// Start the real streaming process using useChat
		handleSubmit(new Event('submit'), {
			body: { email },
			allowEmptySubmit: true
		});
	};

	const getStepDisplay = (): { name: string; key: string }[] => {
		const stepOrder: string[] = [
			'mx_lookup',
			'bounce_check',
			'finding_sender',
			'sending_email'
		];
		const stepLabels = {
			mx_lookup: 'Domain Verification',
			bounce_check: 'Email Address Validation',
			finding_sender: 'Finding Best Route',
			sending_email: 'Sending Test Email'
		} as const;

		return stepOrder.map((stepKey) => {
			const stepData = currentStepData[stepKey];
			return {
				key: stepKey,
				name: stepData?.message || stepLabels[stepKey as keyof typeof stepLabels]
			};
		});
	};

	const handleFeedback = async (type: 'up' | 'down') => {
		setFeedback(type);

		try {
			await fetch(
				`/api/sites/${sessionRef.current?.siteId || 'demo'}/email-deliverability-test/results`,
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						email,
						feedback_type: type,
						feedback_timestamp: new Date().toISOString(),
						test_completed: isComplete,
						test_failed: testFailed,
						success: !testFailed,
						steps_completed: Array.from(completedSteps),
						final_step_data: currentStepData,
						final_result: testFailed ? 'failed' : 'success',
						user_rating: type === 'up' ? 'positive' : 'negative'
					})
				}
			);
		} catch (error) {
			console.error('Failed to send feedback:', error);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && isValidEmail(email)) {
			handleDeliverabilityTest();
		}
	};

	const pricingPlans = [
		{ name: 'Starter', price: '$10', emails: '1,000', id: 'plan_email_1k' },
		{ name: 'Growth', price: '$100', emails: '10,000', id: 'plan_email_10k' },
		{ name: 'Scale', price: '$500', emails: '50,000', id: 'plan_email_50k' },
		{ name: 'Enterprise', price: '$1,000', emails: '100,000', id: 'plan_email_100k' }
	];

	return (
		<section className="relative py-20 px-4 sm:px-6 lg:px-8">
			<div className="max-w-7xl mx-auto">
				<div className="text-center">
					<Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
						🚀 The New Way to Do Cold Email
					</Badge>
					<h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
						<span className="text-emerald-600 block">
							What Cold Email Infra is Best?
						</span>
						Stop Guessing. Try Everything.
					</h1>
					<p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
						While others juggle a few unreliable email providers or servers, we
						instantly route your emails across
						<strong className="text-emerald-600">
							{' '}
							100+ infrastructure partners
						</strong>{' '}
						adapting to spam filter changes hourly.
						<strong className="text-emerald-600">
							{' '}
							Improve deliverability by average of 15 to 30%
						</strong>{' '}
						with setup in just 5 minutes.
					</p>

					{/* Interactive Demo */}
					<div className="max-w-2xl mx-auto mb-8">
						{!showDemo ? (
							<div className="space-y-4">
								<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
									<div className="relative max-w-md w-full">
										<Input
											type="email"
											placeholder="Enter your email - we'll prove it works"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											onKeyDown={handleKeyPress}
											autoComplete="email"
											className="text-lg px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all duration-200 shadow-lg hover:shadow-xl"
										/>
									</div>
									<Button
										size="lg"
										onClick={handleDeliverabilityTest}
										disabled={!isValidEmail(email)}
										className="text-lg px-8 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
									>
										Challenge Us 🎯
										<ArrowRight className="ml-2 h-5 w-5" />
									</Button>
								</div>
								<p className="text-sm text-gray-500">
									☝️ <strong>DON'T BELIEVE US?</strong> Put your email above and
									see if we can land in your PRIMARY inbox (not spam!)
								</p>
							</div>
						) : (
							<Card className="bg-gray-900 text-white border-emerald-500">
								<CardContent className="p-6">
									<div className="space-y-3 text-left font-mono text-sm">
										{getStepDisplay().map((step, index) => {
											const stepData = currentStepData[step.key];

											return (
												<div
													key={step.key}
													className="flex items-center space-x-2"
												>
													{stepData?.status === 'completed' ? (
														<Check className="h-4 w-4 text-green-400" />
													) : stepData?.status === 'failed' ? (
														<div className="h-4 w-4 rounded-full bg-red-500" />
													) : stepData?.status === 'in_progress' ? (
														<Loader2 className="h-4 w-4 text-emerald-400 animate-spin" />
													) : (
														<div className="h-4 w-4 rounded-full border border-gray-600" />
													)}
													<span className="text-white">{step.name}</span>
												</div>
											);
										})}

										{isLoading && getStepDisplay().length === 0 && (
											<div className="flex items-center space-x-2">
												<Loader2 className="h-4 w-4 text-emerald-400 animate-spin" />
												<span className="text-white">
													Starting deliverability test...
												</span>
											</div>
										)}
									</div>

									{isComplete && (
										<div className="mt-6 space-y-4">
											<div className="text-center">
												{testFailed ? (
													<div>
														<p className="text-orange-400 font-semibold mb-2">
															🤔 Oops! Are you trying to trick me?
														</p>
														<p className="text-gray-300 text-sm mb-4">
															It looks like there might be an issue
															with that email address or domain.
														</p>
														<CalendarLink
															href="https://cal.com/team/nosagent/founders"
															target="_blank"
															className="inline-block"
														>
															<Button className="bg-blue-600 hover:bg-blue-700 text-white">
																<Calendar className="h-4 w-4 mr-2" />
																Let's Talk
															</Button>
														</CalendarLink>
													</div>
												) : currentStepData['sending_email']?.status ===
												  'completed' ? (
													<div>
														<p className="text-emerald-400 font-semibold mb-2">
															✓ Email deliverability test completed!
														</p>
														{currentStepData['sending_email']?.data
															?.demo_mode ? (
															<p className="text-yellow-400 text-sm mb-4">
																Demo mode: In production, we would
																send a test email to your inbox.
															</p>
														) : (
															<p className="text-emerald-400 text-sm mb-4">
																Check your inbox for the test email!
															</p>
														)}
													</div>
												) : (
													<p className="text-orange-400 font-semibold mb-4">
														Test completed with some issues. Check
														details above.
													</p>
												)}

												{!testFailed && (
													<>
														<p className="text-white mb-4">
															How did we do?
														</p>
														<div className="flex justify-center space-x-4 mb-4">
															<Button
																onClick={() => handleFeedback('up')}
																variant={
																	feedback === 'up'
																		? 'default'
																		: 'outline'
																}
																className={
																	feedback === 'up'
																		? 'bg-green-600 text-white hover:bg-green-700'
																		: 'border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-white'
																}
															>
																<ThumbsUp className="h-4 w-4 mr-2" />
																Good
															</Button>
															<Button
																onClick={() =>
																	handleFeedback('down')
																}
																variant={
																	feedback === 'down'
																		? 'default'
																		: 'outline'
																}
																className={
																	feedback === 'down'
																		? 'bg-red-600 text-white hover:bg-red-700'
																		: 'border-red-400 text-red-400 hover:bg-red-400 hover:text-white'
																}
															>
																<ThumbsDown className="h-4 w-4 mr-2" />
																Bad
															</Button>
														</div>

														{feedback === 'up' && (
															<p className="text-emerald-400">
																Thanks! 🎉
															</p>
														)}
														{feedback === 'down' && (
															<p className="text-orange-400">
																Too bad! We are always improving our
																sending servers - check back again
																later.
															</p>
														)}
													</>
												)}
											</div>
										</div>
									)}
								</CardContent>
							</Card>
						)}
					</div>

					{/* Pricing Plans */}
					{showDemo && isComplete && (
						<div className="max-w-4xl mx-auto mt-8">
							<h3 className="text-2xl font-bold text-gray-900 mb-6">
								Ready to Get Started? Our pricing starts at $10
							</h3>
							<div className="grid md:grid-cols-4 gap-4">
								{pricingPlans.map((plan) => (
									<Card
										key={plan.id}
										className="border-emerald-200 hover:border-emerald-400 transition-colors"
									>
										<CardContent className="p-4 text-center">
											<h4 className="font-semibold text-gray-900 mb-2">
												{plan.name}
											</h4>
											<div className="text-2xl font-bold text-emerald-600 mb-1">
												{plan.price}
											</div>
											<div className="text-sm text-gray-600 mb-1">
												{plan.emails} emails/month
											</div>
											<div className="text-xs text-emerald-600 mb-3">
												Unlimited mailboxes
											</div>
											<Link
												href="/signup"
												className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 font-medium transition-colors text-center block"
											>
												Get Started
											</Link>
										</CardContent>
									</Card>
								))}
							</div>
						</div>
					)}

					{!showDemo && (
						<div className="mt-8">
							<p className="text-sm text-gray-500 mb-4 font-medium">Works with:</p>
							<div className="flex flex-wrap justify-center items-center gap-6 max-w-4xl mx-auto">
								{logos.map((logo) => (
									<div
										key={logo.name}
										className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-sm border"
									>
										<Image
											src={logo.src}
											alt={`${logo.name} logo`}
											width={24}
											height={24}
											className="rounded-md"
										/>
										<span className="text-gray-700 font-medium">
											{logo.name}
										</span>
									</div>
								))}
								<div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
									<span className="text-gray-500 font-medium">
										and many more...
									</span>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}

// Main component following the home.tsx pattern
function HeroSectionWithSession({ initialSession }: { initialSession: Session | null }) {
	const { data: session, status } = useSession();
	const sessionRef = useRef<Session | null>(initialSession);

	useEffect(() => {
		console.log('HeroSection: Session effect triggered', {
			session,
			status,
			currentSessionRef: sessionRef.current,
			sessionSiteId: session?.siteId,
			sessionRefSiteId: sessionRef.current?.siteId
		});

		if (!session) {
			console.log('HeroSection: Waiting for session', { session, status });
			return;
		}
		if (session && sessionRef.current?.siteId !== session?.siteId) {
			console.log('HeroSection: Session updated', {
				oldSession: sessionRef.current,
				newSession: session
			});
			sessionRef.current = session;
		}
	}, [session, status]);

	// Show static version while session is loading, then interactive when ready
	console.log('HeroSection render decision:', {
		sessionRefExists: !!sessionRef.current,
		sessionRefSiteId: sessionRef.current?.siteId,
		willShowStatic: !sessionRef.current || !sessionRef.current.siteId
	});

	if (!sessionRef.current || !sessionRef.current.siteId) {
		return <StaticHeroSection />;
	}

	return <InteractiveHeroSection sessionRef={sessionRef} />;
}

// Main export that wraps with SessionProvider
export function HeroSection({ session: initialSession }: { session: Session | null }) {
	return (
		<SessionProvider refetchOnWindowFocus={false} refetchInterval={0}>
			<HeroSectionWithSession initialSession={initialSession} />
		</SessionProvider>
	);
}
